import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Clock,
  CheckCircle2,
  Lock,
  Zap,
  BarChart3,
  Share2,
  Bookmark,
  Fingerprint,
  Info,
  ShieldCheck,
  ArrowRight,
  Eye,
  Users
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ShareModal from './ShareModal';
import CalibrationModal from './CalibrationModal';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useOptimisticVote } from '../hooks/useOptimisticVote';
import { usePulse } from '../hooks/usePulse';
import MechanicalOdometer from './poll/MechanicalOdometer';
import CategoryIcon from './poll/CategoryIcon';
import SlideToConfirm from './SlideToConfirm';
import { ProcessingCard } from './poll/PollStateCards';
import PollHero from './poll/PollHero';
import PollVotingOptions from './poll/PollVotingOptions';
import PollResultsView from './poll/PollResultsView';
import PollFooter from './poll/PollFooter';
import { haptics } from '../utils/haptics';

/**
 * PollCard (V3.0 - Professional UX Hierarchy)
 * Optimized for clear visual flow:
 * [Idle/Compact] Question -> Watchers -> Votes -> Button -> Tools
 * [Modal] Question -> Options -> Votes -> Button -> Tools
 * [Results] Question -> Results -> Votes -> Time -> Insights -> Tools
 */
export default function PollCard({
  id,
  question,
  options,
  type = 'single',
  category,
  totalVoters: initialTotalVoters,
  timeLeft: initialTimeLeft,
  img,
  onVote,
  onViewInsights,
  onOpenMenu,
  voted = false,
  isPrivate = false,
  isBounty = false,
  bountyAmount = 50,
  isIncubator = false,
  compact = false,
  inModal = false,
  isMyPoll = false,
  expires_at,
  anonymousId,
  view_count = 0,
  pollExposure = 0,
  pollUpdate = null,
  registerVisibility,
  initialSelection = null,
  isSaved = false,
  is_trending = false,
  onCardClick: onCardClickProp,
  votedStatus = null,
  creatorCriteria = [],
  creator_id = null,
  onBookmarkToggle,
  isHighlighted = false,
  layout = 'grid'
}) {
  const { isVerified } = useAuth();
  const cardRef = React.useRef(null);
  const [selection, setSelection] = useState(initialSelection);
  const [internalStatus, setInternalStatus] = useState(null);
  const [totalVoters, setTotalVoters] = useState(initialTotalVoters);
  const [timeLeft, setTimeLeft] = useState(() => {
    if (initialTimeLeft) return initialTimeLeft;
    if (expires_at) {
      const expiry = new Date(expires_at).getTime();
      const now = Date.now();
      return Math.max(0, Math.floor((expiry - now) / 1000));
    }
    return 86400;
  });

  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [realResults, setRealResults] = useState(null);
  const [isResultsLoading, setIsResultsLoading] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(null);
  
  // VANE Gating State
  const [gateData, setGateData] = useState(null); // { missingCriteria, isDisqualified }
  const [isGateChecking, setIsGateChecking] = useState(false);
  const [liveViewCount, setLiveViewCount] = useState(view_count);

  // Sync state if initialTotalVoters prop changes (e.g. from a list re-fetch or internal repair)
  useEffect(() => {
    setTotalVoters(initialTotalVoters);
  }, [initialTotalVoters]);

  useEffect(() => {
    if (votedStatus === 'revealed' || votedStatus === 'idle') {
      setInternalStatus(null);
    }
  }, [votedStatus]);

  // Derived status: Instant sync with parent props prevents flickering
  const isMyPollEffective = isMyPoll || (anonymousId && creator_id && anonymousId === creator_id);
  const status = internalStatus || (voted || votedStatus === 'revealed' ? 'revealed' : (votedStatus || 'idle'));

  const isOracleUrgent = totalVoters >= 900 && totalVoters < 1000;
  const progressPercent = (totalVoters / 1000) * 100;

  const watchingCount = 0;
  const pulsedTotalVoters = usePulse(totalVoters, 0.05, 6000, 0);
  const pulsedGhosts = pulsedTotalVoters; // Use real total voters for "Verified Votes"

  const isMultiSelect = type === 'multiple' || type === 'rank';
  const hasSelected = selection !== null && (Array.isArray(selection) ? selection.length > 0 : true);

  // Intersection Tracking
  useEffect(() => {
    if (!registerVisibility || !cardRef.current) return;
    const observer = new IntersectionObserver(([entry]) => { registerVisibility(id, entry.isIntersecting); }, { threshold: 0.1 });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [id, registerVisibility]);

  // Session Persistence: remembers an in-progress (not-yet-submitted) option selection per
  // poll ID across reloads/remounts, keyed as `poll_selection_<pollId>` in localStorage. This
  // is cleared in processVoteEffect() once the vote actually fires — it's purely a draft, not
  // the vote itself.
  useEffect(() => {
    const saved = localStorage.getItem(`poll_selection_${id}`);
    if (saved) { 
      try { 
        const parsed = JSON.parse(saved);
        setSelection(parsed); 
      } catch (e) { 
        setSelection(saved); 
      } 
    }
  }, [id]);

  useEffect(() => {
    if (selection !== null) { localStorage.setItem(`poll_selection_${id}`, JSON.stringify(selection)); }
  }, [id, selection]);

  // Industrial Odometer Logic
  const { results: odometerResults, triggerOptimistic, handleServerSync } = useOptimisticVote(id, realResults);

  useEffect(() => { 
    if (pollUpdate) {
      handleServerSync(pollUpdate);
      if (pollUpdate.totalVoters !== undefined) setTotalVoters(pollUpdate.totalVoters);
      if (pollUpdate.viewCount !== undefined) setLiveViewCount(pollUpdate.viewCount);
    } 
  }, [pollUpdate, handleServerSync]);

  useEffect(() => {
    if (status === 'revealed') {
      const fetchResults = async () => {
        // Fetch results immediately. The backend is now buffer-aware, 
        // so it will include our just-cast vote without waiting for the DB flush.

        try { setIsResultsLoading(true); const data = await api.getResults(id); setRealResults(data); }
        catch (err) { console.error("Failed to fetch results:", id, err); }
        finally { setIsResultsLoading(false); }
      };
      fetchResults();
    }
  }, [id, status]);

  const pollPercentages = useMemo(() => {
    const activeResults = odometerResults || realResults;
    if (activeResults) { return options.map((opt, idx) => Math.round(activeResults.weightedPercentages[idx.toString()] || 0)); }
    return options.map(() => 0);
  }, [odometerResults, realResults, options]);

  const leadingIndex = useMemo(() => pollPercentages.indexOf(Math.max(...pollPercentages)), [pollPercentages]);
  const leadingLabel = useMemo(() => {
    const opt = options[leadingIndex];
    if (!opt) return 'No Votes';
    return typeof opt === 'object' ? opt.label : opt;
  }, [options, leadingIndex]);
  const leadingPercent = pollPercentages[leadingIndex] || 0;

  const postVoteInsight = useMemo(() => {
    const categoryInsights = {
      'Tech & Future': "The community anticipates that AI and automation will redefine labor faster than previously projected.",
      'Finance': "Economic sentiment suggests a strong pivot toward decentralized assets as a hedge against inflation.",
      'Society': "Consensus points toward a growing demand for verifiable equity over performative policy changes.",
      'Lifestyle': "Trends show a distinct shift toward minimalism and digital wellness in younger demographics.",
      'Politics': "The data reveals a widening gap between institutional policy and grassroots expectations.",
      'Gaming': "The move toward meta-economies within gaming platforms is now seen as inevitable by the majority."
    };
    return categoryInsights[category] || "The consensus establish a clear preference for local encrypted identity over central control.";
  }, [category]);

  const optionImages = useMemo(() => {
    if (!options || !Array.isArray(options)) return null;
    const imgs = options.map(opt => typeof opt === 'object' ? opt.img : null).filter(Boolean);
    return imgs.length > 0 ? imgs : null;
  }, [options]);

  // Global Expiry State
  useEffect(() => {
    const calculateTime = () => {
      if (expires_at) {
        const expiry = new Date(expires_at).getTime();
        const now = Date.now();
        const diff = Math.max(0, Math.floor((expiry - now) / 1000));
        setTimeLeft(diff);
      } else {
        // For default 24h timer, just decrement
        setTimeLeft(prev => Math.max(0, prev - 1));
      }
    };

    // Calculate immediately on mount
    calculateTime();

    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, [expires_at]);

  const formatTimeLeft = (seconds) => {
    if (seconds <= 0) return "EXPIRED";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Industrial Granularity: Show seconds if less than 2 hours left
    if (hrs < 2) {
      if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
      return `${mins}m ${secs}s`;
    }
    return `${hrs}h ${mins}m`;
  };

  const isExpired = timeLeft <= 0 && expires_at;

  const handleTileClick = (val) => {
    if (status !== 'idle') return;

    setSelection(val);
    haptics.impact();

    if (compact && onCardClickProp) {
      onCardClickProp(val);
    }
  };

  // attemptVote is wired as SlideToVote's `onVote` prop. NOTE: SlideToVote calls `onVote()`
  // with NO arguments (see poll/SlideToVote.js), so `option` and `biometrics` here are always
  // undefined in practice for verified users; the actual choice is read from the `selection`
  // state below instead.
  const attemptVote = async (option, biometrics) => {
    if (!isVerified) {
      // Guest path: hand off to the parent's onVote (App.js's handleVote), which stashes
      // this as a `pending_vote` and opens the enrollment modal; the vote is replayed
      // automatically once the user verifies.
      // `options` here is typically an array of {label, img} objects (not plain strings), so
      // resolve the index via findIndex the same way processVoteEffect() does further down,
      // rather than options.indexOf(label) which would compare a string against objects and
      // always miss (-1) whenever options carry image data.
      const selectedLabel = Array.isArray(selection) ? selection[0] : selection;
      const choiceId = options.findIndex(opt => {
        const label = typeof opt === 'object' ? opt.label : opt;
        return label === selectedLabel;
      }).toString();
      if (onVote) onVote(id, choiceId, null, biometrics);
      return;
    }

    // Check VANE Progressive Gating if poll has criteria
    if (creatorCriteria?.length > 0) {
      setIsGateChecking(true);
      try {
        const res = await api.gateCheck(id, anonymousId);
        if (res.status === 'TRAIT_REQUIRED') {
          setGateData({ missingCriteria: res.missing_criteria, isDisqualified: false });
          return; // Stop vote flow until JIT calibration finishes
        }
        if (res.status === 'DISQUALIFIED') {
          setGateData({ missingCriteria: res.missing_criteria, isDisqualified: true });
          return; // Stop vote flow completely
        }
        // If PASS or HIGH_ENTROPY, allow it through (VANE down-weights high entropy silently)
      } catch (err) {
        console.warn('[VANE] Gate check failed, falling open:', err);
      } finally {
        setIsGateChecking(false);
      }
    }

    processVoteEffect(biometrics);
  };

  const handleCalibrationAnswer = async (traitKey, value) => {
    try {
      await api.updateTraits(anonymousId, { [traitKey]: value });
      
      // Re-run gate check to see if there are more traits or if we failed
      const res = await api.gateCheck(id, anonymousId);
      
      if (res.status === 'TRAIT_REQUIRED') {
        const remaining = res.missing_criteria;
        setGateData(prev => ({ ...prev, missingCriteria: remaining }));
        return true; // We moved to the next question smoothly
      }
      
      if (res.status === 'DISQUALIFIED') {
        setGateData(prev => ({ ...prev, isDisqualified: true }));
        return false; // Show graceful rejection
      }

      // If PASS or HIGH_ENTROPY
      setGateData(null); 
      processVoteEffect(); // Continue the interrupted vote
      return true;

    } catch (err) {
      console.error('[VANE] Failed to save JIT trait:', err);
      return false; 
    }
  };

  // Fires the actual vote after passing (or skipping) VANE gating. Shows a brief 'processing'
  // state, then kicks off the optimistic result-bar nudge (useOptimisticVote) BEFORE the
  // network call resolves, and finally hands off to the parent onVote (App.js's handleVote)
  // which performs the real api.castVote() call.
  const processVoteEffect = (biometrics) => {
    haptics.medium();
    setInternalStatus('processing');

    // 2. Wait for the 'Consensus Engine' (artificial delay purely for UX pacing/animation —
    // no backend call happens during this timeout)
    setTimeout(() => {
      // FIX: Map the selection label/value back to its numeric index for the backend
      const selectedLabel = Array.isArray(selection) ? selection[0] : selection;
      const choiceId = options.findIndex(opt => {
        const label = typeof opt === 'object' ? opt.label : opt;
        return label === selectedLabel;
      }).toString();
      
      triggerOptimistic(choiceId);

      localStorage.removeItem(`poll_selection_${id}`);
      if (onVote) onVote(id, choiceId, biometrics);
    }, 500);
  };

  const handleUnlockResults = async () => {
    try {
      setIsUnlocking(true);
      const res = await api.unlockPoll(anonymousId, id);
      if (res.status === 'success' || res.status === 'already_unlocked') {
        setInternalStatus('revealed');
      }
    } catch (err) {
      console.error("Unlock failed:", err.message);
    } finally {
      setIsUnlocking(false);
    }
  };

  const renderMetricsHUD = () => (
    <div className="flex items-center justify-between px-6 py-3.5 bg-gray-50/50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-850 text-gray-700 dark:text-gray-300 select-none">
      {/* Time Remaining */}
      <div className="group/timer relative flex items-center gap-2 cursor-help">
        <Clock size={16} className="text-orange-500 dark:text-orange-400 animate-pulse" />
        <span className="text-[13px] font-bold text-gray-800 dark:text-gray-200 tabular-nums tracking-wide">{formatTimeLeft(timeLeft)}</span>
        <span className="absolute -top-7 left-0 bg-gray-900 text-white text-2xs font-black uppercase tracking-wider px-2 py-0.5 rounded shadow opacity-0 group-hover/timer:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
          Time Remaining
        </span>
      </div>

      {/* Verified Votes */}
      <div className="group/votes relative flex items-center gap-2 cursor-help">
        <Zap size={13} className="text-[#A855F7] fill-[#A855F7]/10" />
        <div className="flex items-baseline">
          <span className="text-[13px] font-bold text-gray-800 dark:text-gray-200 tabular-nums tracking-wide">
            {pulsedGhosts.toLocaleString()}
          </span>
          <span className="text-2xs font-black text-gray-500 dark:text-gray-500 uppercase ml-1">voters</span>
        </div>
        <span className="absolute -top-7 right-0 bg-gray-900 text-white text-2xs font-black uppercase tracking-wider px-2 py-0.5 rounded shadow opacity-0 group-hover/votes:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50">
          Verified Votes
        </span>
      </div>
    </div>
  );

  if (status === 'processing' || (status === 'revealed' && isResultsLoading && !realResults)) return <ProcessingCard />;
  if (isExpired && !voted && !isMyPollEffective && !isSaved) return null;

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: 1,
        boxShadow: isHighlighted ? [
          "0 0 0px rgba(124, 58, 237, 0)",
          "0 0 30px rgba(124, 58, 237, 0.4)",
          "0 0 0px rgba(124, 58, 237, 0)"
        ] : undefined
      }}
      transition={{ 
        opacity: { duration: 0.15 },
        boxShadow: isHighlighted ? {
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        } : undefined
      }}
      className={`bg-white dark:bg-[#181E2E] overflow-hidden group/card relative rounded-[32px] flex flex-col ${compact ? 'shadow-md hover:shadow-xl' : inModal ? 'h-full' : 'border border-gray-100 dark:border-white/[0.08] shadow-lg'
        } ${isHighlighted ? 'border-primary-600/50 ring-2 ring-primary-600/20' : ''}`}
    >
      <PollHero
        img={id === 'preview' ? (optionImages || img) : img} category={category}
        watchingCount={watchingCount} pulsedGhosts={pulsedGhosts}
        timeLeft={timeLeft} formatTimeLeft={formatTimeLeft}
        inModal={inModal} compact={compact} isTrending={is_trending}
        layout={layout}
      />
      {renderMetricsHUD()}

      <Helmet>
        <title>{question} | Opinion</title>
      </Helmet>

      {/* 2. CONTENT CONTAINER (VERTICAL STACK) */}
      <div className={`relative w-full flex-1 flex flex-col overflow-hidden bg-white dark:bg-[#181E2E]`}>

        {/* COMPACT VIEW (DIRECT ACTION LAYOUT) */}
        {compact && (
          <div className="flex flex-col h-full bg-white dark:bg-[#181E2E]">
            <div className="px-6 py-8 space-y-6 max-w-2xl mx-auto w-full">
              {/* Question */}
              <h2 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white italic tracking-tight leading-snug text-center">
                {question}
              </h2>

              {/* Interactive Voting Options */}
              <div className="pt-2">
                <PollVotingOptions
                  type={type}
                  options={options}
                  selection={selection}
                  handleTileClick={handleTileClick}
                  inModal={false}
                  compact={true}
                  showResults={false}
                />
              </div>

              {/* Cryptographic Selection Indicator (Subtle Cue) */}
              <div className="flex items-center justify-center gap-2 py-1 opacity-40 group-hover:opacity-60 transition-opacity">
                <Lock size={10} className="text-primary-600" />
                <span className="text-2xs font-black uppercase tracking-[0.2em] text-gray-500">Secure Cryptographic Selection</span>
              </div>
            </div>

            {/* Universal Minimalist Footer */}
            <PollFooter
              id={id}
              onShare={() => setIsShareModalOpen(true)}
              onOpenMenu={onOpenMenu}
            />
          </div>
        )}

        {/* MODAL / FULL VIEW (UNIFIED VERTICAL STACK) */}
        {!compact && (
          <div className={`flex flex-col flex-1 overflow-y-auto p-4 md:p-6 bg-white dark:bg-[#181E2E] rounded-b-3xl`}>
            <div className="space-y-4 h-full flex flex-col max-w-2xl mx-auto w-full">
              {/* Oracle Urgency Bar (Re-engineered Hierarchy) */}
              {isOracleUrgent && (
                <div className="w-full bg-primary-600/5 dark:bg-white/5 rounded-2xl p-4 border border-primary-600/20 animate-pulse">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xs font-black uppercase tracking-[0.2em] text-primary-600">Institutional Consensus</span>
                    <span className="text-2xs font-black tabular-nums text-primary-600">{totalVoters} / 1000 Verified Records</span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercent}%` }}
                      className="h-full bg-gradient-to-r from-primary-600 to-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]" 
                    />
                  </div>
                </div>
              )}
              {/* 1. QUESTION */}
              <h1 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white italic tracking-tight leading-snug text-center max-w-[95%] mx-auto">
                {question}
              </h1>

              <div className="flex-1 flex flex-col mt-6">
                {status === 'idle' && (
                  <div className={`space-y-6 flex-1 flex flex-col justify-center ${inModal ? '' : 'animate-in fade-in slide-in-from-bottom-8 duration-700'}`}>
                    {isExpired && isSaved ? (
                      <div className="py-20 px-10 bg-gray-50/50 dark:bg-gray-800/40 rounded-3xl border-4 border-dashed border-gray-100 dark:border-gray-800 text-center space-y-10 shadow-inner">
                        <Lock size={60} className="mx-auto text-amber-500/20" />
                        <h3 className="text-5xl font-black italic tracking-tighter text-white">RESULTS ARE LOCKED</h3>
                        <button onClick={handleUnlockResults} className="px-16 py-8 bg-amber-500 text-white rounded-2xl font-black text-3xl uppercase italic shadow-2xl hover:scale-105 transition-all">Unlock Engine</button>
                      </div>
                    ) : (
                      <div className="space-y-8">
                        {/* OPTIONS SELECTION (HIERARCHY #2 in Modal) */}
                        <PollVotingOptions
                          type={type} options={options} selection={selection}
                          handleTileClick={handleTileClick}
                          handleCastVote={(val) => {
                            setSelection(val);
                            attemptVote();
                          }}
                          inModal={inModal}
                          hoveredRating={hoveredRating} setHoveredRating={setHoveredRating}
                        />
                      </div>
                    )}
                  </div>
                )}

                {status === 'revealed' && (
                  <div className="space-y-8 animate-in fade-in zoom-in-95 duration-700">
                    <PollResultsView
                      type={type}
                      options={options} pollPercentages={pollPercentages}
                      leadingIndex={leadingIndex} leadingLabel={leadingLabel}
                      leadingPercent={leadingPercent} showAllResults={showAllResults}
                      setShowAllResults={setShowAllResults} isMultiSelect={isMultiSelect}
                      selection={selection} postVoteInsight={postVoteInsight}
                      onViewInsights={onViewInsights}
                      inModal={inModal}
                      totalVoters={totalVoters}
                    />
                  </div>
                )}
              </div>

              {/* 3. PRIMARY ACTION (Modal Context) */}
              <div className="mt-4 mb-4 flex flex-col items-center w-full">
                {isMyPollEffective ? (
                  <div className="w-full p-6 rounded-[28px] bg-primary-600/5 border border-primary-600/20 flex flex-col items-center gap-4 text-center">
                    <div className="flex items-center gap-3 bg-primary-600/10 px-4 py-1.5 rounded-full">
                      <ShieldCheck size={14} className="text-primary-600" />
                      <span className="text-2xs font-black uppercase tracking-[0.2em] text-primary-600">Creator Console</span>
                    </div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 max-w-[80%] italic">
                      You are the architect of this consensus. Your identity is shielded from the voting pool to maintain impartiality.
                    </p>
                    <div className="flex gap-2 w-full">
                      <button
                        disabled
                        title="Editing a live poll isn't available yet"
                        className="flex-1 py-3 bg-gray-100 dark:bg-white/5 text-gray-500 rounded-xl font-black text-2xs uppercase tracking-widest cursor-not-allowed border border-dashed border-gray-200 dark:border-white/10"
                      >
                        Edit — Coming Soon
                      </button>
                      <button
                        onClick={onViewInsights}
                        className="flex-1 py-3 bg-primary-600 hover:bg-[#8B5CF6] text-white rounded-xl font-black text-2xs uppercase tracking-widest transition-all shadow-lg"
                      >
                        Deep Analytics
                      </button>
                    </div>
                  </div>
                ) : (
                  status === 'idle' && !isExpired && (
                    <div className="w-full max-w-xl">
                      {hasSelected ? (
                        <SlideToConfirm
                          onConfirm={attemptVote}
                          idleLabel={isMultiSelect ? "Slide to Confirm" : "Slide to Lock In"}
                          draggingLabel="Securing Vote..."
                          completeLabel="Vote Cast!"
                          ariaLabel={isMultiSelect ? "Slide to confirm your selections, or press Enter" : "Slide to lock in your vote, or press Enter"}
                          disabled={isGateChecking}
                        />
                      ) : (
                        <div className="w-full py-6 rounded-[32px] font-black text-3xl uppercase tracking-tighter italic flex items-center justify-center gap-6 bg-gray-50 dark:bg-white/5 text-gray-300 dark:text-white/10 border border-dashed border-gray-200 dark:border-white/10 cursor-not-allowed opacity-60 shadow-inner">
                          <span>Select Choice</span>
                          <Fingerprint size={32} className="opacity-20" />
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* 4. UNIVERSAL MINIMALIST FOOTER (Stand-alone or Modal) */}
        {!compact && (
          <PollFooter
            id={id}
            onShare={() => setIsShareModalOpen(true)}
            onOpenMenu={onOpenMenu}
            inModal={inModal}
            isSaved={isSaved}
            onBookmarkToggle={onBookmarkToggle}
          />
        )}
      </div>

      <ShareModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)} 
        pollData={{ 
          id, 
          question, 
          category, 
          totalVoters, 
          status: status === 'revealed' ? 'archived' : 'active', 
          options, 
          topOptionLabel: leadingLabel, 
          topOptionPercent: leadingPercent, 
          timeLeftLabel: formatTimeLeft(timeLeft),
          voted: status === 'revealed',
          hasVoted: status === 'revealed',
          isMyPoll: isMyPollEffective,
          expires_at,
          mainResults: options.map((opt, i) => ({ 
            label: typeof opt === 'object' ? opt.label : opt, 
            percent: pollPercentages[i] || 0 
          }))
        }} 
        activeFilter={status === 'revealed' ? 'result' : 'main'}
      />
      
      <CalibrationModal
        isOpen={!!gateData}
        missingCriteria={gateData?.missingCriteria || []}
        isDisqualified={gateData?.isDisqualified || false}
        onAnswer={handleCalibrationAnswer}
        onClose={() => setGateData(null)}
      />
    </motion.div>
  );
}